import React, { useState, KeyboardEvent, useEffect, useRef } from 'react';
import { Mail, X, Check, Send, PlusCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

interface ShareProfileEmailDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  onShareSuccess?: () => void;
}

const ShareProfileEmailDropdown: React.FC<ShareProfileEmailDropdownProps> = ({ isOpen, onClose, username, onShareSuccess }) => {
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [sendCopyToSelf, setSendCopyToSelf] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAddEmail = () => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }

    if (emails.includes(trimmed)) {
      setError('Email already added');
      return;
    }

    setEmails([...emails, trimmed]);
    setEmailInput('');
    setError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter(e => e !== email));
  };

  const handleSend = async () => {
    if (emails.length === 0 && !emailInput) {
      setError('Please add at least one recipient');
      return;
    }

    let finalEmails = [...emails];
    if (emailInput.trim()) {
      const trimmed = emailInput.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmed) && !emails.includes(trimmed)) {
        finalEmails.push(trimmed);
      } else if (!emailRegex.test(trimmed)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setIsSending(true);
    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      await axios.post('/api/v1/users/share_profile', {
        emails: finalEmails,
        link_kits: sendCopyToSelf
      }, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      toast.success('Profile shared successfully!');
      if (onShareSuccess) onShareSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 z-[50] flex flex-col items-start bg-white"
      style={{
        width: '100%',
        minHeight: '16.5rem',
        marginTop: '0.125rem',
        boxSizing: 'border-box',
        background: '#FFFFFF',
        border: '0.0625rem solid #D1D5DB',
        boxShadow: '0 1.1875rem 2.5rem -0.5rem rgba(0, 0, 0, 0.32), 0 0.375rem 0.75rem -0.125rem rgba(0, 0, 0, 0.16)',
        borderRadius: '0.375rem',
        top: '100%'
      }}
    >
      {/* Frame 125 - Main Content Area */}
      <div
        className="flex flex-col items-start w-full"
        style={{ padding: '0.75rem', gap: '0.5rem', boxSizing: 'border-box' }}
      >
        {/* Frame 120 - Email Input Row */}
        <div
          className="flex flex-row justify-between items-center w-full"
          style={{
            height: '3rem',
            padding: '0.5rem',
            gap: '8.5rem', // gap: 136px
            borderBottom: '0.0625rem dashed #D1D5DB',
            boxSizing: 'border-box'
          }}
        >
          {/* Frame 604 -> Frame 118 & Frame 605 */}
          <div className="flex flex-row items-center flex-1 min-w-0" style={{ gap: '0.5rem' }}>
            {/* Tag Label Primary */}
            <span
              className="font-medium flex items-center text-center whitespace-nowrap"
              style={{ fontSize: '0.875rem', lineHeight: '1.3125rem', color: '#374151', width: '3.75rem' }}
            >
              Email to:
            </span>
            {/* Frame 605 -> Tag Label Primary & Line 1 */}
            <div className="flex flex-row items-center border-l border-blue-400 pl-2" style={{ gap: '0.0625rem' }}>
              <input
                type="text"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="name@example.com"
                className="border-none outline-none font-medium"
                style={{
                  fontSize: '0.875rem',
                  lineHeight: '1.3125rem',
                  color: '#374151',
                  width: '10.9375rem',
                  background: 'transparent'
                }}
              />
            </div>
          </div>

          {/* Mini Circle Primary - Add Button */}
          <div
            onClick={handleAddEmail}
            className="flex flex-row justify-center items-center bg-white  rounded-full cursor-pointer transition-colors"
            style={{ width: '2rem', height: '2rem', boxSizing: 'border-box' }}
          >
            <PlusCircle size={18} color="#3B82F6" strokeWidth={2.5} />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="flex flex-row justify-center items-center self-stretch"
            style={{
              boxSizing: 'border-box',
              padding: '0.375rem 0rem',
              gap: '0.25rem',
              width: '100%',
              height: '1.75rem',
              background: '#FFF5F5',
              border: '0.0625rem solid rgba(246, 60, 63, 0.2)',
              borderRadius: '0.375rem',
              margin: '0.25rem 0'
            }}
          >
            <XCircle size={16} color="#F63C3F" strokeWidth={1.25} />
            <span
              style={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '0.625rem',
                lineHeight: '0.8125rem',
                textAlign: 'center',
                letterSpacing: '0.02em',
                color: '#CC0003'
              }}
            >
              {error}
            </span>
          </div>
        )}

        {/* Frame 316 - Recipient Tags Area */}
        <div
          className="flex flex-row flex-wrap items-start content-start w-full overflow-y-auto"
          style={{ padding: '0.625rem 0', gap: '0.625rem 0.375rem', minHeight: '6.5rem', boxSizing: 'border-box' }}
        >
          {emails.map(email => (
            <div
              key={email}
              className="flex flex-row justify-center items-center bg-white border border-gray-300 rounded-full"
              style={{ padding: '0.5rem 1.25rem', gap: '0.375rem', height: '2.3125rem', boxSizing: 'border-box' }}
            >
              <span
                className="font-medium text-center truncate"
                style={{ fontSize: '0.875rem', color: '#0D1621', maxWidth: '11.6875rem' }}
              >
                {email}
              </span>
              <button
                onClick={() => removeEmail(email)}
                className="flex items-center justify-center"
              >
                <X size={14} color="#576C89" strokeWidth={1.5} />
              </button>
            </div>
          ))}
          {emails.length === 0 && !emailInput && (
            <span className="text-gray-400 italic text-xs px-2 self-center">Add recipients...</span>
          )}
        </div>
      </div>

      {/* Frame 603 - Footer Area */}
      <div
        className="flex flex-row items-center w-full"
        style={{
          padding: '1.25rem',
          gap: '0.625rem',
          height: '4.75rem',
          background: '#F9FAFB',
          borderTop: '0.0625rem solid #E2E8F0',
          boxSizing: 'border-box'
        }}
      >
        {/* Check Option */}
        <div
          className="flex flex-row items-center flex-1 cursor-pointer"
          onClick={() => setSendCopyToSelf(!sendCopyToSelf)}
          style={{ gap: "0.25rem" }}
        >
          <div
            className="flex items-center justify-center border-[1.5px]"
            style={{
              width: "1.125rem",
              height: "1.125rem",
              borderRadius: "0.125rem",
              border: `0.125rem solid ${sendCopyToSelf ? "#3C82F6" : "#4B5563"}`,
              background: sendCopyToSelf ? "#3C82F6" : "transparent",
              boxSizing: "border-box",
            }}
          >
            {sendCopyToSelf && <Check size={14} color="#FFFFFF" strokeWidth={3} />}
          </div>
          <span
            className="font-medium truncate"
            style={{
              fontSize: "0.875rem",
              lineHeight: "1.3125rem",
              color: "#374151",
              letterSpacing: "-0.005em",
            }}
          >
            Send a copy to myself
          </span>
        </div>

        {/* Primary - Send Button */}
        <button
          onClick={handleSend}
          disabled={isSending}
          className="flex flex-row justify-center items-center shadow-md bg-blue-600 rounded-md transition-all shrink-0"
          style={{
            padding: '0.5rem 1rem',
            gap: '0.625rem',
            width: '6.125rem',
            height: '2.25rem',
            boxSizing: 'border-box'
          }}
        >
          <Send size={20} color="#FFFFFF" strokeWidth={2} />
          <span
            className="font-semibold text-center text-white"
            style={{ fontSize: '0.875rem', lineHeight: '1.3125rem', width: '2.25rem' }}
          >
            {isSending ? '...' : 'Send'}
          </span>
        </button>
      </div>
    </div>
  );
};

export default ShareProfileEmailDropdown;
